import { uiSurface } from "shared/ui/uiSurface"
import { Add, CallMerge, People, Phone, Search, SearchOff, Upload } from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material"
import { GridPaginationModel, GridSortModel } from "@mui/x-data-grid"
import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { useGetClientsQuery } from "../../apis/appApi"
import { ClientDuplicatesDialog } from "./ClientDuplicatesDialog"
import { ClientImportDialog } from "./ClientImportDialog"
import { PhoneBulkUpdateDialog } from "./PhoneBulkUpdateDialog"
import { CustomersGrid } from "./CustomersGrid"
import { CustomerDetailDrawer } from "./CustomerDetailDrawer"
import { CustomerFormDialog } from "./CustomerFormDialog"

export const CustomersPage: FC = () => {
  const { t } = useTranslation()

  const [search, setSearch] = useState("")
  const [showArchived, setShowArchived] = useState(false)
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  })
  const [sortField, setSortField] = useState("id")
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc")
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null)
  const [formDialogOpen, setFormDialogOpen] = useState(false)
  const [editingClientId, setEditingClientId] = useState<number | null>(null)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [phoneBulkDialogOpen, setPhoneBulkDialogOpen] = useState(false)
  const [duplicatesDialogOpen, setDuplicatesDialogOpen] = useState(false)

  // Snackbar state — shared across form dialog and detail drawer
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: "success" | "error"
  }>({ open: false, message: "", severity: "success" })

  const showSuccess = useCallback((message: string) => {
    const isError = message.toLowerCase().includes("error") || message.toLowerCase().includes("erreur") || message.toLowerCase().includes("failed")
    setSnackbar({ open: true, message, severity: isError ? "error" : "success" })
  }, [])

  const { data: response, isLoading } = useGetClientsQuery({
    page: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
    search: search || undefined,
    sortField,
    sortOrder,
  })

  const sortModel: GridSortModel = sortField
    ? [{ field: sortField, sort: sortOrder }]
    : []

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      if (model.length > 0) {
        setSortField(model[0].field)
        setSortOrder(model[0].sort ?? "asc")
      } else {
        setSortField("id")
        setSortOrder("desc")
      }
      setPaginationModel((m) => ({ ...m, page: 0 }))
    },
    [],
  )

  const rows = response?.data ?? []
  const rowCount = response?.total ?? 0

  const filteredRows = showArchived ? rows : rows.filter((r) => !r.archived)
  const filteredRowCount = showArchived ? rowCount : filteredRows.length

  const handleRowClick = useCallback((id: number) => {
    setSelectedClientId(id)
  }, [])

  const handleDrawerClose = useCallback(() => {
    setSelectedClientId(null)
  }, [])

  const handleEdit = useCallback((clientId: number) => {
    setEditingClientId(clientId)
    setFormDialogOpen(true)
  }, [])

  const handleCreate = useCallback(() => {
    setEditingClientId(null)
    setFormDialogOpen(true)
  }, [])

  const handleFormClose = useCallback(() => {
    setFormDialogOpen(false)
    setEditingClientId(null)
  }, [])

  return (
    <Box data-testid="customers-page" sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Page header */}
      <Box {...uiSurface("UI-DLV2-081")} sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 }, pb: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "flex-start" }}
          spacing={1.5}
        >
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="baseline">
              <Typography variant="h4" fontWeight={700}>
                {t("clients.title")}
              </Typography>
              {!isLoading && (
                <Typography variant="body2" color="text.secondary">
                  {t("clients.clientsCount", { count: rowCount })}
                </Typography>
              )}
            </Stack>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("clients.subtitle")}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CallMerge />}
              onClick={() => setDuplicatesDialogOpen(true)}
              data-testid="customers-duplicates-btn"
              sx={{ flexShrink: 0 }}
            >
              {t("clients.duplicates.button")}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Phone />}
              onClick={() => setPhoneBulkDialogOpen(true)}
              data-testid="customers-phone-bulk-btn"
              sx={{ flexShrink: 0 }}
            >
              {t("clients.phoneBulk.button")}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Upload />}
              onClick={() => setImportDialogOpen(true)}
              data-testid="customers-import-btn"
              sx={{ flexShrink: 0 }}
            >
              {t("clients.import.button")}
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={handleCreate}
              data-testid="customers-create-btn"
              sx={{ flexShrink: 0 }}
            >
              {t("clients.create")}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Filters bar */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={2}
        sx={{ px: { xs: 2, md: 3 }, pb: 2 }}
      >
        <TextField
          size="small"
          placeholder={t("clients.search")}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPaginationModel((m) => ({ ...m, page: 0 }))
          }}
          data-testid="customers-search-input"
          sx={{ width: { xs: "100%", md: 280 } }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
            },
          }}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showArchived}
              onChange={(_, v) => setShowArchived(v)}
              data-testid="customers-archived-switch"
            />
          }
          label={
            <Typography variant="body2">
              {t("clients.showArchived")}
            </Typography>
          }
        />
      </Stack>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "hidden", px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 } }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : filteredRows.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            {rowCount === 0 && !search ? (
              <Stack data-testid="customers-empty-state" alignItems="center" spacing={2} sx={{ maxWidth: 360 }}>
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    bgcolor: "grey.100",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <People sx={{ fontSize: 36, color: "grey.400" }} />
                </Box>
                <Typography variant="h6" fontWeight={600} color="text.primary">
                  {t("clients.emptyDay.title")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  {t("clients.emptyDay.description")}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={handleCreate}
                >
                  {t("clients.create")}
                </Button>
              </Stack>
            ) : (
              <Stack data-testid="customers-empty-filter" alignItems="center" spacing={1.5} sx={{ maxWidth: 320 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    bgcolor: "grey.100",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <SearchOff sx={{ fontSize: 28, color: "grey.400" }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                  {t("clients.emptyFilter.title")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  {t("clients.emptyFilter.description")}
                </Typography>
              </Stack>
            )}
          </Box>
        ) : (
          <Paper
            variant="outlined"
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              borderRadius: 2,
            }}
          >
            <CustomersGrid
              rows={filteredRows}
              loading={isLoading}
              rowCount={filteredRowCount}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              onRowClick={handleRowClick}
            />
          </Paper>
        )}
      </Box>

      {/* Detail drawer */}
      <CustomerDetailDrawer
        clientId={selectedClientId}
        open={!!selectedClientId}
        onClose={handleDrawerClose}
        onEdit={handleEdit}
        onSuccess={showSuccess}
      />

      {/* Create/Edit dialog */}
      <CustomerFormDialog
        open={formDialogOpen}
        onClose={handleFormClose}
        onSuccess={showSuccess}
        clientId={editingClientId}
      />

      {/* Import dialog */}
      <ClientImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onSuccess={showSuccess}
      />

      {/* Phone bulk update dialog */}
      <PhoneBulkUpdateDialog
        open={phoneBulkDialogOpen}
        onClose={() => setPhoneBulkDialogOpen(false)}
        onSuccess={showSuccess}
      />

      {/* Duplicates dialog */}
      <ClientDuplicatesDialog
        open={duplicatesDialogOpen}
        onClose={() => setDuplicatesDialogOpen(false)}
        onSuccess={showSuccess}
      />

      {/* Toast notifications */}
      <Snackbar
        data-testid="customers-snackbar"
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
